// Small pieces shared by Page Access and the User Manager.
import React from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { stringToColor } from "../../../utilities";

// tone: "base" — set in code (read-only), "extra" — granted here,
// "custom" — a role created on Page Access.
export const RoleChip = ({ role, tone = "extra", onRemove, disabled, title }) => {
  const toneClass =
    tone === "base"
      ? "app-surface"
      : tone === "custom"
      ? "bg-violet-500/10"
      : "bg-sky-500/10";
  return (
    <span
      title={title}
      style={tone === "base" ? undefined : { borderColor: stringToColor(role) }}
      className={`${toneClass} inline-flex min-h-[1.75rem] max-w-full items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${
        tone === "base" ? "border-[color:var(--app-surface-border)]" : ""
      }`}
    >
      <span className="truncate">{role}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Remove ${role}`}
          className="-mr-1 inline-flex h-5 w-5 items-center justify-center rounded-full transition hover:bg-rose-500/15 hover:text-rose-600 disabled:opacity-40 dark:hover:text-rose-300"
        >
          <CloseRoundedIcon sx={{ fontSize: 14 }} />
        </button>
      )}
    </span>
  );
};

export const UserAvatar = ({ user, size = "md" }) => {
  const dims = size === "lg" ? "h-14 w-14" : size === "sm" ? "h-7 w-7" : "h-10 w-10";
  return (
    <span
      className={`${dims} relative inline-block shrink-0 overflow-hidden rounded-full bg-slate-500/10 ring-2 ring-sky-500/25`}
    >
      <img
        src={`/api/profile/image?id=${user?.id || "default"}`}
        alt=""
        loading="lazy"
        className="h-full w-full object-cover"
      />
    </span>
  );
};

// Pill tabs; `tabs` = [{ value, label, count? }].
export const TabBar = ({ tabs, value, onChange, label }) => (
  <div
    role="tablist"
    aria-label={label}
    className="app-surface inline-flex max-w-full gap-1 overflow-x-auto rounded-full border border-[color:var(--app-surface-border)] p-1"
  >
    {tabs.map((tab) => {
      const active = tab.value === value;
      return (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(tab.value)}
          className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition ${
            active
              ? "bg-sky-600 text-white shadow-sm"
              : "text-[color:var(--app-text)] hover:bg-[color:var(--app-hover)]"
          }`}
        >
          {tab.label}
          {tab.count != null && (
            <span
              className={`rounded-full px-1.5 text-xs tabular-nums ${
                active ? "bg-white/20" : "bg-slate-500/15"
              }`}
            >
              {tab.count}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

// message: { type: "success" | "error" | "info", text }
export const Notice = ({ message, className = "" }) => {
  if (!message) return null;
  const tone =
    message.type === "success"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
      : message.type === "error"
      ? "bg-rose-500/10 text-rose-700 dark:text-rose-300"
      : "bg-sky-500/10 text-sky-800 dark:text-sky-200";
  return (
    <p
      role={message.type === "error" ? "alert" : "status"}
      className={`rounded-xl px-3 py-2 text-sm ${tone} ${className}`}
    >
      {message.text}
    </p>
  );
};

const ONLINE_MS = 5 * 60 * 1000;

export const isOnline = (user) =>
  Boolean(user?.last_seen) && Date.now() - new Date(user.last_seen).getTime() < ONLINE_MS;

export const timeAgo = (value) => {
  if (!value) return "Never";
  const ms = Date.now() - new Date(value).getTime();
  if (Number.isNaN(ms)) return "—";
  const minutes = Math.round(ms / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(value).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatWhen = (value) =>
  value
    ? new Date(value).toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

// Who can open a page right now, from its code roles + its grant.
export const peopleWhoCanOpen = (page, grant, users) => {
  const roles = new Set([...(page?.roles || []), ...(grant?.roles || [])]);
  const direct = new Set(grant?.users || []);
  return users.filter(
    (u) =>
      !u.deactivated &&
      ((u.id && direct.has(u.id)) || (u.roles || []).some((r) => roles.has(r)))
  );
};
